import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";

// Pages - organized imports
import { 
  HomePage, AboutPage, ContactPage, CareersPage, PressPage, PartnersPage, BulaCoffeeClubPage, SharePage, BulaWrappedPage, MonthlyReportPage 
} from "@/pages/public";
import FAQPage from "@/pages/public/FAQPage";
import BlogPage from "@/pages/public/BlogPage";
import BlogPostPage from "@/pages/public/BlogPostPage";
import PaymentPage from "@/pages/public/PaymentPage";
import BakedGoodsDisplay from "@/pages/public/BakedGoodsDisplay";
import MenuDisplayBoard from "@/pages/public/MenuDisplayBoard";
import { 
  MenuPage, OrderPage, BuildYourOwnPage, KioskPage, POSPage, TrainingPOSPage, ServerPage, PickupPage, OrderDisplayPage, FavoritesPage, CartRecoveryPage 
} from "@/pages/ordering";
import KioskQueuePage from "@/pages/ordering/KioskQueuePage";
import StaffOrderDisplay from "@/pages/ordering/StaffOrderDisplay";
import CustomerFacingDisplay from "@/pages/ordering/CustomerFacingDisplay";
import { 
  ProfilePage, OrderHistoryPage, LoyaltyPage, ViralAccountPage 
} from "@/pages/account";
import { 
  PrivacyPolicyPage, TermsOfUsePage, CookiePolicyPage 
} from "@/pages/legal";
import { 
  PartnerProgramPage, PartnerSignupPage, PartnerDashboardPage, PartnerFlyerPage 
} from "@/pages/partner";
import ReferralSignupPage from "@/pages/referral/ReferralSignupPage";
import ReferralDashboardPage from "@/pages/referral/ReferralDashboardPage";
import SalesRepGuidePage from "@/pages/SalesRepGuidePage";
import StaffPOSGuidePage from "@/pages/StaffPOSGuidePage";
import { CampaignPage } from "@/pages/marketing";
import AdminPage from "@/pages/AdminPage";
import SystemResetPage from "@/pages/admin/SystemResetPage";
import CateringPage from "@/pages/CateringPage";
import CateringMenuPDF from "@/pages/CateringMenuPDF";

// Staff Portal Pages
import {
  StaffLoginPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  StaffProfilePage,
  StaffSchedulePage,
  StaffTimeOffPage,
  StaffDevicePage,
  ScheduleManagementPage,
  WeeklyRosterPage
} from "@/pages/staff";

// Staff Clock Page
import StaffClockPage from "@/pages/StaffClockPage";
import MemberLoginPage from "@/pages/MemberLoginPage";
import PaymentCallbackPage from "@/pages/PaymentCallbackPage";
import PrintChecklistPage from "@/pages/public/PrintChecklistPage";
import StaffWorkforceGuidePage from "@/pages/public/StaffWorkforceGuidePage";
import PrintServerPage from "@/pages/PrintServerPage";

// Security Components
import SecureNetworkGate from "@/components/SecureNetworkGate";
import CashierGuidePage from "@/pages/public/CashierGuidePage";
import TimeClockGuidePage from "@/pages/public/TimeClockGuidePage";

// WiFi Welcome Page
import WifiWelcomePage from "@/pages/WifiWelcomePage";

// Individual Staff Guides
import StaffGettingStartedGuide from "@/pages/public/guides/StaffGettingStartedGuide";
import StaffTimeClockGuide from "@/pages/public/guides/StaffTimeClockGuide";
import StaffScheduleGuide from "@/pages/public/guides/StaffScheduleGuide";
import StaffTimeOffGuide from "@/pages/public/guides/StaffTimeOffGuide";

// KDS Pages
import { 
  PinLoginPage, 
  CounterDisplayPage, 
  BaristaStationPage, 
  KitchenStationPage, 
  JuiceBarStationPage,
  ServerTabletPage,
  KDSClearPage,
  CounterStationPage
} from "@/pages/kds";

// Components
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import ChatWidget from "@/components/chat/ChatWidget";

// Context Providers
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { ContentProvider } from "@/context/ContentContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { PushNotificationProvider } from "@/context/PushNotificationContext";
import { OrderStatusProvider } from "@/context/OrderStatusContext";
import { AnalyticsProvider } from "@/context/AnalyticsContext";
import "./App.css";

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
      <AuthProvider>
        <FavoritesProvider>
          <CartProvider>
            <ContentProvider>
              <PushNotificationProvider>
                <OrderStatusProvider>
                  <BrowserRouter>
                    <ScrollToTop />
                    <AnalyticsProvider>
                      <Toaster 
                        position="top-right" 
                        richColors 
                        toastOptions={{
                          style: {
                            fontFamily: 'Outfit, sans-serif',
                          },
                        }}
                      />
                      <CookieConsent />
                      <Routes>
                        <Route element={<Layout />}>
                          <Route path="/" element={<HomePage />} />
                          <Route path="/menu" element={<MenuPage />} />
                          <Route path="/build-your-own" element={<BuildYourOwnPage />} />
                          <Route path="/about" element={<AboutPage />} />
                          <Route path="/contact" element={<ContactPage />} />
                          <Route path="/faq" element={<FAQPage />} />
                          <Route path="/blog" element={<BlogPage />} />
                          <Route path="/blog/:slug" element={<BlogPostPage />} />
                          <Route path="/order" element={<OrderPage />} />
                          <Route path="/order-history" element={<OrderHistoryPage />} />
                          <Route path="/loyalty" element={<LoyaltyPage />} />
                          <Route path="/favorites" element={<FavoritesPage />} />
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/cookies" element={<CookiePolicyPage />} />
                          <Route path="/terms-of-use" element={<TermsOfUsePage />} />
                          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                          <Route path="/careers" element={<CareersPage />} />
                          <Route path="/press" element={<PressPage />} />
                          <Route path="/partners" element={<PartnersPage />} />
                          <Route path="/bula-coffee-club" element={<BulaCoffeeClubPage />} />
                          <Route path="/share" element={<SharePage />} />
                          <Route path="/rewards" element={<ViralAccountPage />} />
                          <Route path="/my-account" element={<MemberLoginPage />} />
                          <Route path="/account" element={<Navigate to="/my-account" replace />} />
                          <Route path="/member-login" element={<MemberLoginPage />} />
                          <Route path="/bula-wrapped" element={<BulaWrappedPage />} />
                          <Route path="/monthly-report" element={<MonthlyReportPage />} />
                          <Route path="/pay/:orderNumber" element={<PaymentPage />} />
                          <Route path="/partner-program" element={<PartnerProgramPage />} />
                          <Route path="/partner-signup" element={<PartnerSignupPage />} />
                          <Route path="/partner" element={<PartnerDashboardPage />} />
                          {/* New Commission-Based Referral Partner System */}
                          <Route path="/referral-signup" element={<ReferralSignupPage />} />
                          <Route path="/referral-dashboard" element={<ReferralDashboardPage />} />
                        </Route>
                        <Route path="/admin/system-reset" element={<SystemResetPage />} />
                        <Route path="/admin/*" element={<AdminPage />} />
                        <Route path="/kiosk" element={<KioskPage />} />
                        <Route path="/wifi" element={<WifiWelcomePage />} />
                        <Route path="/wifi-welcome" element={<WifiWelcomePage />} />
                        <Route path="/pos" element={<POSPage />} />
                        <Route path="/customer-display" element={<CustomerFacingDisplay />} />
                        <Route path="/training-pos" element={<TrainingPOSPage />} />
                        <Route path="/server" element={<ServerPage />} />
                        <Route path="/pickup" element={<PickupPage />} />
                        <Route path="/display" element={<OrderDisplayPage />} />
                        <Route path="/kiosk-queue" element={<KioskQueuePage />} />
                        <Route path="/staff-display" element={<StaffOrderDisplay />} />
                        <Route path="/display1" element={<MenuDisplayBoard />} />
                        <Route path="/display2" element={<MenuDisplayBoard />} />
                        <Route path="/display3" element={<MenuDisplayBoard />} />
                        <Route path="/baked-display/:displayNumber" element={<BakedGoodsDisplay />} />
                        <Route path="/join/:campaignId" element={<CampaignPage />} />
                        <Route path="/cart/recover/:token" element={<CartRecoveryPage />} />
                        {/* Standalone printable pages - no navbar/footer */}
                        <Route path="/partner-flyer" element={<PartnerFlyerPage />} />
                        <Route path="/sales-rep-guide" element={<SalesRepGuidePage />} />
                        <Route path="/staff-pos-guide" element={<StaffPOSGuidePage />} />
                        <Route path="/print-checklist" element={<PrintChecklistPage />} />
                        <Route path="/staff-guide" element={<StaffWorkforceGuidePage />} />
                        <Route path="/cashier-guide" element={<CashierGuidePage />} />
                        <Route path="/time-clock-guide" element={<TimeClockGuidePage />} />
                        {/* Individual Staff Guides - Bite-sized PDFs */}
                        <Route path="/staff-guide/getting-started" element={<StaffGettingStartedGuide />} />
                        <Route path="/staff-guide/time-clock" element={<StaffTimeClockGuide />} />
                        <Route path="/staff-guide/schedule" element={<StaffScheduleGuide />} />
                        <Route path="/staff-guide/time-off" element={<StaffTimeOffGuide />} />
                        {/* Staff guide aliases for consistent /staff/* URLs */}
                        <Route path="/staff/cashier-guide" element={<CashierGuidePage />} />
                        <Route path="/staff/timeclock-guide" element={<TimeClockGuidePage />} />
                        {/* Convenient Short URLs - Redirect to KDS pages */}
                        <Route path="/counter" element={<Navigate to="/kds/counter" replace />} />
                        <Route path="/barista" element={<Navigate to="/kds/barista" replace />} />
                        <Route path="/kitchen" element={<Navigate to="/kds/kitchen" replace />} />
                        <Route path="/juicer" element={<Navigate to="/kds/juicer" replace />} />
                        {/* KDS Station Pages */}
                        <Route path="/kds/login" element={<PinLoginPage />} />
                        <Route path="/kds/counter" element={<CounterStationPage />} />
                        <Route path="/kds/barista" element={<BaristaStationPage />} />
                        <Route path="/kds/kitchen" element={<KitchenStationPage />} />
                        <Route path="/kds/juicer" element={<JuiceBarStationPage />} />
                        <Route path="/kds/server" element={<ServerTabletPage />} />
                        <Route path="/kds/clear" element={<KDSClearPage />} />
                        <Route path="/kds/gatherer" element={<CounterDisplayPage />} />
                        {/* Staff Portal Pages - Open to logged-in staff */}
                        <Route path="/staff" element={<Navigate to="/staff/login" replace />} />
                        <Route path="/staff/login" element={<StaffLoginPage />} />
                        <Route path="/staff/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/staff/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/staff/profile" element={<StaffProfilePage />} />
                        <Route path="/staff/schedule" element={<StaffSchedulePage />} />
                        <Route path="/staff/manage-schedule" element={
                          <SecureNetworkGate featureName="Schedule Management">
                            <ScheduleManagementPage />
                          </SecureNetworkGate>
                        } />
                        <Route path="/staff/roster" element={<WeeklyRosterPage />} />
                        <Route path="/staff/time-off" element={<StaffTimeOffPage />} />
                        <Route path="/staff/device" element={
                          <SecureNetworkGate featureName="Device Registration">
                            <StaffDevicePage />
                          </SecureNetworkGate>
                        } />
                        {/* Staff Clock App - MUST be on cafe network */}
                        <Route path="/staff/clock" element={
                          <SecureNetworkGate featureName="Time Clock">
                            <StaffClockPage />
                          </SecureNetworkGate>
                        } />
                        {/* Print Server - For Mac/PC connected to receipt printer */}
                        <Route path="/print-server" element={
                          <SecureNetworkGate featureName="Print Server">
                            <PrintServerPage />
                          </SecureNetworkGate>
                        } />
                        <Route path="/staff-clock" element={
                          <SecureNetworkGate featureName="Time Clock">
                            <StaffClockPage />
                          </SecureNetworkGate>
                        } />
                        {/* M-PAiSA Payment Callback */}
                        <Route path="/payment/callback" element={<PaymentCallbackPage />} />
                        {/* Catering Portal */}
                        <Route path="/catering" element={<CateringPage />} />
                        <Route path="/catering/menu-pdf" element={<CateringMenuPDF />} />
                      </Routes>
                      <ChatWidget />
                    </AnalyticsProvider>
                  </BrowserRouter>
                </OrderStatusProvider>
              </PushNotificationProvider>
            </ContentProvider>
          </CartProvider>
        </FavoritesProvider>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
